/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { QueryApiService } from 'src/app/shared/services/customer-api/query-api.service';
import { RoutingService } from 'src/app/service/routing.service';
import { ConfigService } from 'src/app/service/config.service';
import { SubEventsService } from 'src/app/shared/services/data-api/sub-events.service';
import { DateUtils } from 'src/app/shared/utils/date.utils';
import { QueryEditorComponent } from '../../news-query/query-editor/query-editor.component';
import { QueryNameComponent } from '../../news-query/query-name/query-name.component';
import { ReportStateService } from 'src/app/shared/services/report-state.service';
import { AiSearchService } from 'src/app/shared/services/ai-search/ai-search.service';

@Component({
  selector: 'app-query-title',
  templateUrl: './query-title.component.html',
  styleUrls: ['./query-title.component.scss'],
})
export class QueryTitleComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  @Input() titolo: any;
  @ViewChild('queryMenuTrigger') queryMenuTrigger: MatMenuTrigger;
  @ViewChild('titleBar') titleBarRef: ElementRef<HTMLElement> | undefined;

  isNarrow = window.innerWidth < 1550;
  // Toggle the long-label prefix ("Due Diligence") on the reports button
  // via the actual bar width measured by ResizeObserver.
  isBarNarrow = false;
  private barResizeObserver: ResizeObserver | undefined;
  private static readonly BAR_NARROW_THRESHOLD = 900;
  params: any = {};

  isLoadingEvents = false;
  scoreEventsCount: number | null = null;
  scoreEventsTimeframe: number | null = null;
  scoreEventsDelta: number | null = null;
  private previousValue: any;

  constructor(
    public routing: RoutingService,
    private queryApi: QueryApiService,
    public config: ConfigService,
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    public reportStateService: ReportStateService,
    private aiSearchService: AiSearchService,
    private subEvents: SubEventsService,
    private zone: NgZone,
    private destroyRef: DestroyRef,
  ) {
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const timeframeChanged =
          this.previousValue && params.time !== this.previousValue.time;
        this.params = params;
        if (timeframeChanged && this.titolo) {
          this.getScoreEvents();
        }
        this.previousValue = params;
      });
  }

  @HostListener('window:resize')
  onResize(): void {
    this.isNarrow = window.innerWidth < 1550;
  }

  ngAfterViewInit(): void {
    if (!this.titleBarRef || typeof ResizeObserver === 'undefined') return;
    this.barResizeObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      const narrow = width < QueryTitleComponent.BAR_NARROW_THRESHOLD;
      if (narrow !== this.isBarNarrow) {
        this.zone.run(() => (this.isBarNarrow = narrow));
      }
    });
    this.barResizeObserver.observe(this.titleBarRef.nativeElement);
  }

  ngOnDestroy(): void {
    this.barResizeObserver?.disconnect();
  }

  get label(): string {
    if (this.routing.isQuery()) {
      return this.params.id === this.params.groupId ? 'Collection' : 'Query';
    }
    return 'Search';
  }

  get name(): string {
    if (this.routing.isQuery()) {
      return this.queryApi.query?.name ?? '';
    }
    return this.queryApi.query?.name || this.titolo?.entity?.name || '';
  }

  /**
   * True when what is loaded is a collection rather than one query. Its `query`
   * is a synthetic OR of every query it holds (built in the editor's save path),
   * so editing that as a single query and saving would collapse the whole
   * collection into one. Read from the data, not the URL: a deep link can carry
   * a collection id with no groupId, which the template's param check misses.
   */
  get isCollection(): boolean {
    const uid = this.queryApi.query?.uid;
    return !!uid && !!this.queryApi.querys?.some((c) => c.uid === uid);
  }

  get collectionQueries(): any[] {
    const { groupId } = this.params;
    if (!groupId) return [];
    const collection = this.queryApi.querys?.find((c) => c.uid === groupId);
    return collection?.content ?? [];
  }

  openQueryMenu(): void {
    this.queryMenuTrigger?.openMenu();
  }

  navigateToQuery(query: any): void {
    this.router.navigate([], {
      queryParams: {
        id: query.uid,
        groupId: this.params.groupId,
        queryid: null,
        newsfeedFromQuery: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  navigateToCollection(): void {
    this.router.navigate([], {
      queryParams: {
        id: this.params.groupId,
        groupId: this.params.groupId,
        queryid: null,
        newsfeedFromQuery: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  get sentimentDelta(): number {
    return (this.titolo?.sentiment?.sentiment_delta ?? 0) * 100;
  }

  // Display value for the sentiment-delta pill. Shows one decimal unless the
  // decimal is 0 (then displays the integer); falls back to "0.1" when the raw
  // delta is non-zero but rounds to 0.0.
  get displaySentimentDelta(): string {
    if (!this.sentimentDelta) return '';
    const display = Math.abs(this.sentimentDelta).toFixed(1);
    if (display === '0.0') return '0.1';
    return display.endsWith('.0') ? display.slice(0, -2) : display;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['titolo']) {
      const previousValue = changes['titolo'].previousValue;
      const currentValue = changes['titolo'].currentValue;

      // Execute logic if the value has actually changed (including first load)
      if (JSON.stringify(previousValue) !== JSON.stringify(currentValue)) {
        this.executeOnChanges();
      }
    }
  }
  executeOnChanges() {
    if (this.titolo) {
      this.reportStateService.checkStatuses({ mode: 'query' });
      this.getScoreEvents();
    }
  }

  // Events count + delta vs previous period. Mirrors chart-title; the sub-events
  // service auto-routes through the v2 query endpoint when on a query/search.
  private getScoreEvents(): void {
    this.isLoadingEvents = true;
    this.scoreEventsCount = null;
    this.scoreEventsDelta = null;

    // The events count is the essential value — render it (and clear loading) as
    // soon as it arrives. The previous-period delta is a best-effort pill fetched
    // separately, so a stalled/empty delta request can never hang the count.
    this.subEvents
      .fetchSubEventsTotal(this.params)
      .pipe(catchError(() => of(0)))
      .subscribe((current) => {
        this.scoreEventsCount = current;
        this.scoreEventsTimeframe = this.params.time
          ? DateUtils.fromTo(this.params.time).period
          : null;
        this.isLoadingEvents = false;
        this.fetchScoreEventsDelta(current);
      });
  }

  // Best-effort previous-period delta for the events pill. Independent of the
  // loading state — failure just leaves the delta null.
  private fetchScoreEventsDelta(current: number): void {
    const previousTime = this.computePreviousPeriodTime();
    if (!previousTime) return;
    this.subEvents
      .fetchSubEventsTotal(this.params, previousTime)
      .pipe(catchError(() => of(null)))
      .subscribe((previous) => {
        this.scoreEventsDelta = previous !== null ? current - previous : null;
      });
  }

  private computePreviousPeriodTime(): string | null {
    if (!this.params.time) return null;
    const fromto = DateUtils.fromTo(this.params.time);
    if (!fromto?.dateFrom || !fromto?.period) return null;
    const startDate = new Date(fromto.dateFrom);
    if (isNaN(startDate.getTime())) return null;
    const prevStart = new Date(startDate);
    prevStart.setDate(prevStart.getDate() - fromto.period);
    const fmt = (d: Date) => d.toISOString().split('T')[0];
    return `${fmt(prevStart)}--${fmt(startDate)}`;
  }

  get displayEventsDelta(): number {
    return Math.abs(this.scoreEventsDelta ?? 0);
  }

  onEdit(): void {
    // Pick a query first — the dropdown beside the name lists them.
    if (this.isCollection) {
      this.openQueryMenu();
      return;
    }
    const authQuery = this.queryApi.query;
    const isQueryMode = this.routing.isQuery();
    const isSearchMode = this.routing.isSearch();
    const isAiContext =
      !isQueryMode && !!this.aiSearchService.lastGeneratedQueryData;
    const dialogData = authQuery
      ? {
          uid: isQueryMode ? authQuery.uid : undefined,
          name: authQuery.name,
          description: authQuery.description ?? '',
          query: authQuery.query,
          groups: authQuery.groups ?? [],
          _isAiGenerated: isSearchMode || isAiContext || undefined,
        }
      : this.aiSearchService.lastGeneratedQueryData;

    this.dialog.open(QueryEditorComponent, { data: dialogData });
  }

  onSave(action: string): void {
    this.dialog.open(QueryNameComponent, {
      data: {
        action,
        query: this.titolo?.entity,
      },
    });
  }
}
